import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  Clock,
  CreditCard,
  Gauge,
  Hand,
  Info,
  MailWarning,
  MessageSquare,
  PauseCircle,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  notificationService,
  type Notification,
  type NotificationCategory,
} from '@/services/notificationService';

// ---------------------------------------------------------------------------
// Kind registry — maps kind → { icon, deep-link builder }
// Fallback: /settings for unknown kinds.
// ---------------------------------------------------------------------------
interface KindMeta {
  icon: LucideIcon;
  deepLink: (payload: Record<string, unknown>) => string;
}

const KIND_REGISTRY: Record<string, KindMeta> = {
  conversation_escalated: {
    icon: MessageSquare,
    deepLink: () => '/conversations',
  },
  prospect_stalled: {
    icon: Clock,
    deepLink: (p) => (p.contact_id ? `/conversations/${p.contact_id}` : '/conversations'),
  },
  lead_qualified: {
    icon: UserCheck,
    deepLink: (p) => (p.lead_id ? `/leads/${p.lead_id}` : '/leads'),
  },
  // F154 FR6 — lead captured but the person refused/withheld their name: the
  // lead row exists (status stays engaged, never promoted to qualified). Same
  // deep link as lead_qualified — the owner still lands on the lead record.
  lead_captured_no_name: {
    icon: UserPlus,
    deepLink: (p) => (p.lead_id ? `/leads/${p.lead_id}` : '/leads'),
  },
  trial_cap_hit: {
    icon: CreditCard,
    deepLink: () => '/settings',
  },
  monthly_cap_hit: {
    icon: CreditCard,
    deepLink: () => '/settings',
  },
  quota_warning_80pct: {
    icon: Gauge,
    deepLink: () => '/settings',
  },
  paused_message_held: {
    icon: PauseCircle,
    deepLink: (p) => (p.contact_id ? `/conversations/${p.contact_id}` : '/conversations'),
  },
  // F192 — a DM arrived while the owner had assumed control (bot silenced
  // for that contact). Same deep-link shape as paused_message_held.
  silenced_message_received: {
    icon: Hand,
    deepLink: (p) => (p.contact_id ? `/conversations/${p.contact_id}` : '/conversations'),
  },
  // F164 — a DM Meta never webhooked to us (Instagram filters some messages
  // into hidden requests and suppresses the messaging webhook for those). The
  // daily reconciliation sweep finds it by diffing the Graph conversations
  // edge against our inbound rows. No contact/conversation row was ever
  // created for it, so there is deliberately no per-contact deep link — the
  // conversations list is the best we can offer until the owner replies
  // manually on Instagram.
  // F202 — reused with `reason === 'billing_blocked'`: the DM *did* reach us
  // and a contact row exists, so that variant deep-links to the thread.
  missed_conversation: {
    icon: MailWarning,
    deepLink: (p) =>
      p.reason === 'billing_blocked' && p.contact_id
        ? `/conversations/${p.contact_id}`
        : '/conversations',
  },
  // F202 FR2 — inbound arrived while the subscription was inactive; the bot
  // is stopped. Billing lives in /settings.
  billing_blocked_inbound: {
    icon: CreditCard,
    deepLink: () => '/settings',
  },
};

const FALLBACK_META: KindMeta = {
  icon: Info,
  deepLink: () => '/settings',
};

function getKindMeta(kind: string): KindMeta {
  return KIND_REGISTRY[kind] ?? FALLBACK_META;
}

// F154 FR6 — legacy fallback: pre-F154 `lead_qualified` rows can carry a null
// or missing `display_name` (a lead qualified before the name gate existed).
// i18next interpolates that as an empty string, so the owner saw a dangling
// "Lead qualificado: ". Render the no-name copy instead — the deep link is
// unchanged (both kinds route to the same lead record).
function resolveKindKey(kind: string, payload: Record<string, unknown>): string {
  const hasDisplayName =
    typeof payload.display_name === 'string' && payload.display_name.trim().length > 0;
  if (kind === 'lead_qualified' && !hasDisplayName) {
    return 'lead_captured_no_name';
  }
  // F164 — the Graph API can omit the Instagram handle on a missed-DM sweep
  // hit; i18next would otherwise interpolate an empty string and render a
  // dangling "@". Render the no-handle copy instead — same kind, same deep
  // link. Unlike `lead_captured_no_name` this variant gets NO KIND_REGISTRY
  // entry, because the backend never emits it as a real `kind` — it exists
  // only as a translation-key swap.
  const hasUsername = typeof payload.username === 'string' && payload.username.trim().length > 0;
  // F202 — same kind, different cause: the message arrived but the bot was
  // blocked by an inactive subscription. Branch on `reason` before the F164
  // copy, which wrongly says the DM never reached us.
  if (kind === 'missed_conversation' && payload.reason === 'billing_blocked') {
    return hasUsername
      ? 'missed_conversation_billing_blocked'
      : 'missed_conversation_billing_blocked_no_username';
  }
  if (kind === 'billing_blocked_inbound' && !hasUsername) {
    return 'billing_blocked_inbound_no_username';
  }
  if (kind === 'missed_conversation' && !hasUsername) {
    return 'missed_conversation_no_username';
  }
  // F192 — silence-gate notification: the payload may omit `username` (e.g.
  // Meta didn't send a handle). Same no-handle fallback shape as above.
  if (kind === 'silenced_message_received' && !hasUsername) {
    return 'silenced_message_received_no_username';
  }
  // F201 — a `conversation_escalated` row whose reason is `sensitive` is a
  // *personal* message the bot forwarded (grief, illness, a sad update), not
  // another hot lead. The generic "Conversa escalada" would have the owner
  // open it expecting a prospect. Same key-swap shape as the fallbacks above:
  // no new kind, so KIND_REGISTRY and the deep link stay untouched.
  if (kind === 'conversation_escalated' && payload.reason === 'sensitive') {
    return 'conversation_escalated_sensitive';
  }
  return kind;
}

// ---------------------------------------------------------------------------
// Simple relative time formatter
// ---------------------------------------------------------------------------
function formatRelative(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// NotificationRow
// ---------------------------------------------------------------------------
interface NotificationRowProps {
  notification: Notification;
}

function NotificationRow({ notification }: NotificationRowProps) {
  const { t } = useTranslation();
  const { kind, category, payload_json, seen_at, created_at } = notification;
  const meta = getKindMeta(kind);
  const Icon = meta.icon;
  const href = meta.deepLink(payload_json);
  const unseen = seen_at === null;

  return (
    <Link
      to={href}
      data-testid={`notification-row-${notification.id}`}
      className={`flex items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-muted ${
        unseen
          ? `border-l-2 ${category === 'escalation' ? 'border-l-warning' : 'border-l-border'}`
          : ''
      }`}
    >
      <span
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          category === 'escalation' ? 'bg-warning/10' : 'bg-muted'
        }`}
      >
        <Icon
          className={`h-4 w-4 ${category === 'escalation' ? 'text-warning-foreground' : 'text-muted-foreground'}`}
          aria-hidden="true"
        />
      </span>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className={`text-sm truncate ${unseen ? 'font-medium' : ''}`}>
          {t(
            `notifications.kind.${resolveKindKey(kind, payload_json)}`,
            payload_json as Record<string, string>
          )}
        </span>
        <span className="text-xs text-muted-foreground">{formatRelative(created_at)}</span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// NotificationSection
// ---------------------------------------------------------------------------
interface NotificationSectionProps {
  category: NotificationCategory;
  items: Notification[];
  onMarkAll: () => void;
}

function NotificationSection({ category, items, onMarkAll }: NotificationSectionProps) {
  const { t } = useTranslation();
  const hasUnseen = items.some((n) => n.seen_at === null);

  return (
    <section data-testid={`notifications-section-${category}`} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-base font-semibold">{t(`notifications.section.${category}`)}</h2>
        {hasUnseen && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid={`mark-all-seen-${category}`}
            onClick={onMarkAll}
          >
            {t('notifications.mark_all_seen')}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div data-testid={`${category}-empty-state`}>
          {category === 'escalation' ? (
            <EmptyState icon={AlertTriangle} title={t('notifications.empty.escalation')} />
          ) : (
            <EmptyState icon={Bell} title={t('notifications.empty.system')} />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((n) => (
            <NotificationRow key={n.id} notification={n} />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// NotificationsPage
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 500;

export function NotificationsPage() {
  const { t } = useTranslation();

  const [escalations, setEscalations] = useState<Notification[] | null>(null);
  const [systemItems, setSystemItems] = useState<Notification[] | null>(null);
  const [error, setError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAll = useCallback(async () => {
    setError(false);
    try {
      const [esc, sys] = await Promise.all([
        notificationService.list({ category: 'escalation', page_size: 25 }),
        notificationService.list({ category: 'system', page_size: 25 }),
      ]);
      setEscalations(esc.items);
      setSystemItems(sys.items);

      // Debounced mark-seen for all unseen items now visible
      const unseenIds = [
        ...esc.items.filter((n) => n.seen_at === null).map((n) => n.id),
        ...sys.items.filter((n) => n.seen_at === null).map((n) => n.id),
      ];
      if (unseenIds.length > 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          notificationService.markSeen({ ids: unseenIds }).catch(() => {
            // best-effort
          });
        }, DEBOUNCE_MS);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchAll]);

  const handleMarkAll = async (category: NotificationCategory) => {
    try {
      await notificationService.markSeen({ all: true, category });
      await fetchAll();
    } catch {
      // best-effort
    }
  };

  const loading = escalations === null && systemItems === null && !error;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader title={t('notifications.title')} subtitle={t('notifications.subtitle')} />

      {loading ? (
        <div data-testid="notifications-skeleton" className="flex flex-col gap-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-6 w-32 mt-2" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : error ? (
        <div
          data-testid="notifications-error"
          className="flex flex-col items-center gap-4 rounded-lg border border-dashed p-12 text-center"
          role="alert"
        >
          <p className="text-sm text-muted-foreground">{t('notifications.error')}</p>
          <Button type="button" variant="outline" onClick={() => void fetchAll()}>
            {t('notifications.retry')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <NotificationSection
            category="escalation"
            items={escalations ?? []}
            onMarkAll={() => void handleMarkAll('escalation')}
          />
          <NotificationSection
            category="system"
            items={systemItems ?? []}
            onMarkAll={() => void handleMarkAll('system')}
          />
        </div>
      )}
    </div>
  );
}
